import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric, BiometryType, BiometricOptions } from '@capgo/capacitor-native-biometric';
import { IAuthAccessToken } from '@app-auth/data-access/entities/auth.interface';
import { LocalStorageService } from '@app-services/localStorage.service';

/**
 * All biometric-login info persisted in a SINGLE localStorage object (key `biometric`),
 * instead of several loose keys. Survives logout (preserved by `clearAllData`).
 */
export interface IBiometricInfo {
  /** Biometric login is enrolled. */
  enabled: boolean;
  /** Email the biometric enrollment belongs to (re-offered when a different email logs in). */
  email: string | null;
  /** Last account's PersonId — used by the welcome card avatar. */
  personId: string | null;
  /** Display name on the welcome card. */
  userName: string | null;
  /** Display email on the welcome card. */
  userEmail: string | null;
  /** Whether the email has more than one account (drives "Entrar com uma conta diferente"). */
  hasMultipleAccounts: boolean;
}

const INFO_KEY = 'biometric';

const INFO_DEFAULTS: IBiometricInfo = {
  enabled: false,
  email: null,
  personId: null,
  userName: null,
  userEmail: null,
  hasMultipleAccounts: false,
};

/**
 * Infra wrapper around the native biometric plugin and the device secure storage
 * (Keychain / Keystore), plus the persisted biometric info object. Stateless — all app
 * state and orchestration live in {@link BiometricStore}. The native plugin calls are a
 * safe no-op on non-native platforms.
 */
@Injectable({ providedIn: 'root' })
export class BiometricService {
  private readonly localStorageService = inject(LocalStorageService);

  /** Keychain/Keystore namespace for the stored session. */
  private readonly server = 'com.trei.no.biometric';

  public isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** True only when the device has usable biometric hardware enrolled. */
  public async isAvailable(): Promise<boolean> {
    if (!this.isNative()) return false;
    try {
      const result = await NativeBiometric.isAvailable({ useFallback: true });
      return result.isAvailable && result.biometryType !== BiometryType.NONE;
    } catch {
      return false;
    }
  }

  /** Prompts the native biometric dialog; rejects if it fails or is cancelled. */
  public verifyIdentity(options: BiometricOptions): Promise<void> {
    return NativeBiometric.verifyIdentity(options);
  }

  /** Writes the session to the Keychain/Keystore (the `password` slot holds the session JSON). */
  public async storeSession(session: IAuthAccessToken): Promise<void> {
    await this.deleteSession();
    await NativeBiometric.setCredentials({
      username: session.PersonId ?? 'session',
      password: JSON.stringify(session),
      server: this.server,
    });
  }

  /** Reads and deserializes the session previously written by {@link storeSession}. */
  public async readSession(): Promise<IAuthAccessToken> {
    const credentials = await NativeBiometric.getCredentials({ server: this.server });
    return JSON.parse(credentials.password) as IAuthAccessToken;
  }

  /** Removes the stored session from the Keychain/Keystore. */
  public async deleteSession(): Promise<void> {
    if (!this.isNative()) return;
    try {
      await NativeBiometric.deleteCredentials({ server: this.server });
    } catch {
    }
  }

  /** The persisted biometric info, fully defaulted (never undefined). */
  public readInfo(): IBiometricInfo {
    const raw = this.localStorageService.getItem(INFO_KEY);
    if (!raw) return { ...INFO_DEFAULTS };
    try {
      return { ...INFO_DEFAULTS, ...JSON.parse(raw) };
    } catch {
      return { ...INFO_DEFAULTS };
    }
  }

  /** Merges a partial update into the stored biometric info object. */
  public patchInfo(partial: Partial<IBiometricInfo>): void {
    this.localStorageService.setItem(INFO_KEY, JSON.stringify({ ...this.readInfo(), ...partial }));
  }

  /** Removes the whole biometric info object. */
  public clearInfo(): void {
    this.localStorageService.removeItem(INFO_KEY);
  }
}
